import { REWARDS_ART } from '@/assets/rewards';

/**
 * How an achievement medallion is put together.
 *
 * The FRAME is the step: the backend says which tier (ladder tier index, or
 * the onboarding step) a badge belongs to, and the winged frame follows from
 * that — Figma node 13332:170574 draws one frame per step.
 *
 * The GLYPH inside is the client's: it is drawn per achievement (TP/SL gets the
 * TP/SL roundel, the first deposit gets the coins, …), keyed by the achievement
 * code the backend sends. The backend's own art is only a fallback for an
 * achievement nothing is drawn for here yet.
 */

export const BADGE_FRAME_TIERS = ['ice', 'silver', 'gold', 'onyx', 'royal'] as const;

export type BadgeFrameTier = (typeof BADGE_FRAME_TIERS)[number];

const FRAME_SRC: Record<BadgeFrameTier, string> = {
  ice: REWARDS_ART['badge-frame-ice.webp'],
  silver: REWARDS_ART['badge-frame-silver.webp'],
  gold: REWARDS_ART['badge-frame-gold.webp'],
  onyx: REWARDS_ART['badge-frame-onyx.webp'],
  royal: REWARDS_ART['badge-frame-royal.webp'],
};

// ------------------------------------------------------------------ frames

/**
 * Step → frame. Step 0 is onboarding; ladder tiers run 1..6. The spec maps by
 * tier index, never by rarity: 1,2 → A, 3 → B, 4+ → C, so a fourth variant can
 * be added later without a backend change.
 */
const FRAME_BY_TIER: readonly BadgeFrameTier[] = ['ice', 'ice', 'gold', 'royal'];

export function badgeFrameForTier(tierIndex: number | null | undefined): string {
  if (tierIndex === null || tierIndex === undefined || tierIndex < 1) return FRAME_SRC.ice;
  return FRAME_SRC[FRAME_BY_TIER[Math.min(tierIndex, FRAME_BY_TIER.length) - 1]];
}

/**
 * The bespoke rare badges carry no step, only `rarity` — an opaque string, so
 * the known vocabularies are mapped here and anything unrecognised falls back
 * to the base frame rather than inventing a tier.
 */
const TIER_BY_RARITY: Record<string, BadgeFrameTier> = {
  '1': 'ice',
  '2': 'silver',
  '3': 'gold',
  '4': 'onyx',
  '5': 'royal',
  COMMON: 'ice',
  BRONZE: 'ice',
  UNCOMMON: 'silver',
  SILVER: 'silver',
  RARE: 'gold',
  GOLD: 'gold',
  EPIC: 'onyx',
  PLATINUM: 'onyx',
  LEGENDARY: 'royal',
  // What the service sends (public-profile notes, 25.09: COMMON / RARE / EPIC / LEGEND).
  LEGEND: 'royal',
  MYTHIC: 'royal',
  DIAMOND: 'royal',
};

export function badgeFrameFor(rarity: string | undefined): string {
  const tier = rarity ? TIER_BY_RARITY[rarity.trim().toUpperCase()] : undefined;
  return FRAME_SRC[tier ?? 'ice'];
}

/** Group of the onboarding task badges — step 0, the base frame whatever their rarity. */
export const ONBOARDING_GROUP = 'ONBOARDING';

/**
 * Group of the season task badges. They all wear the season wreath (Figma
 * 13878:193343, champagne gold); every one is `COMMON`, so rarity says nothing.
 */
export const SEASON_GROUP = 'SEASON';

const SEASON_FRAME = REWARDS_ART['badge-frame-seasonal.webp'];

export function badgeFrameForBadge(badge: {
  /** The step, when the caller knows it (a ladder quest's tier index). */
  tierIndex?: number | null;
  group?: string | null;
  code?: string | null;
  rarity?: string;
}): string {
  if (badge.tierIndex !== undefined && badge.tierIndex !== null) {
    return badgeFrameForTier(badge.tierIndex);
  }
  if (badge.group === ONBOARDING_GROUP) return badgeFrameForTier(0);
  if (badge.group === SEASON_GROUP) return SEASON_FRAME;
  // The bespoke rare badges: no step, only a rarity. Nothing else reaches here
  // — `GET /badges` stopped carrying the derived `LADDER_TIER` rows (V381), and
  // a tier achievement is composed at its call site, which passes `tierIndex`.
  return badgeFrameFor(badge.rarity);
}

// ------------------------------------------------------------------ glyphs

/**
 * Well the glyph sits in: Figma's Ellipse 27268 (node 12882:155346), a navy
 * fade that dims the frame's gem without hiding its facets.
 */
const BADGE_WELL = 'radial-gradient(circle, #001326 0%, #001326 24%, rgba(0,19,38,0) 100%)';

/** The season wreath's well (Figma 13878:193346): the same fade, in brown. */
const SEASON_BADGE_WELL = 'radial-gradient(circle, #3a1f00 0%, #3a1f00 24%, rgba(29,24,0,0) 100%)';

export function badgeWellFor(group: string | null | undefined): string {
  return group === SEASON_GROUP ? SEASON_BADGE_WELL : BADGE_WELL;
}

/** Glyph Figma shows on a badge nothing else is drawn for. */
export const BADGE_GLYPH = REWARDS_ART['badge-glyph.webp'];

/**
 * Glyph per achievement, keyed by the code the backend sends (Figma
 * 13218:194302 names its frames after the onboarding task codes). Ladder
 * achievements go in here by ladder code as their art is drawn; until then
 * they fall back to the ladder's `imageUrl`, then the generic glyph.
 *
 * The last two onboarding badges hold each other's artwork in the Figma file —
 * the shield reads "TP SL" and the phone reads "PROMO CODE" — so they are keyed
 * by what they depict.
 */
const ACHIEVEMENT_GLYPH: Record<string, string> = {
  FIRST_DEPOSIT: REWARDS_ART['quests/glyphs/first-deposit.webp'],
  FIRST_ORDER: REWARDS_ART['quests/glyphs/first-order.webp'],
  FIRST_CLOSED_DEAL: REWARDS_ART['quests/glyphs/first-closed-deal.webp'],
  FIRST_LIMIT_ORDER: REWARDS_ART['quests/glyphs/first-limit-order.webp'],
  SET_TP_SL: REWARDS_ART['quests/glyphs/set-tp-sl.webp'],
  APP_PROMO_REDEEM: REWARDS_ART['quests/glyphs/app-promo-redeem.webp'],
};

/**
 * The achievement behind a badge code: a derived tier badge `TIER_<ladder>_<N>`
 * is the ladder's achievement, any other code is its own.
 */
export function achievementCodeOf(code: string | null | undefined): string | null {
  if (!code) return null;
  const upper = code.trim().toUpperCase();
  const tier = upper.match(/^TIER_(.+)_\d+$/);
  return tier ? tier[1] : upper;
}

/** The glyph drawn here for the achievement, or null when there is none yet. */
export function achievementGlyphFor(code: string | null | undefined): string | null {
  const achievement = achievementCodeOf(code);
  return achievement ? (ACHIEVEMENT_GLYPH[achievement] ?? null) : null;
}
